<script setup>
// ===== Imports =====
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, helpers } from '@vuelidate/validators';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  onlyOneVariable,
  validUrl,
  phoneFormat,
  noDuplicateQuickReplyTexts,
} from '../validations';
import { VALIDATION_CONSTANTS, BUTTONS_INFO_LIST } from '../constants';
import CollapsibleSection from 'dashboard/components/ui/CollapsibleSection.vue';

// ===== Props & Emits =====
const props = defineProps({
  language: {
    type: String,
    default: 'en_US',
  },
  category: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
// ===== Data =====
const buttons = ref([]);
const showAddMenu = ref(false);
// ===== Computed =====
const quickReplyButtons = computed(() => {
  return buttons.value.filter(b => b.type === 'QUICK_REPLY');
});
const ctaButtons = computed(() => {
  return buttons.value.filter(b =>
    ['URL', 'PHONE_NUMBER', 'COPY_CODE', 'VOICE_CALL'].includes(b.type)
  );
});
const urlButtons = computed(() => {
  return buttons.value.filter(b => b.type === 'URL');
});
const phoneButtons = computed(() => {
  return buttons.value.filter(b => b.type === 'PHONE_NUMBER');
});
const copyCodeButtons = computed(() => {
  return buttons.value.filter(b => b.type === 'COPY_CODE');
});
const voiceCallButtons = computed(() => {
  return buttons.value.filter(b => b.type === 'VOICE_CALL');
});
const canAddMoreButtons = computed(() => {
  return buttons.value.length < VALIDATION_CONSTANTS.MAX_TOTAL_BUTTONS;
});
const canAddQuickReply = computed(() => {
  return (
    quickReplyButtons.value.length <
    VALIDATION_CONSTANTS.MAX_QUICK_REPLY_BUTTONS
  );
});
const canAddUrl = computed(() => {
  return urlButtons.value.length < VALIDATION_CONSTANTS.MAX_URL_BUTTONS;
});
const canAddPhone = computed(() => {
  return phoneButtons.value.length < VALIDATION_CONSTANTS.MAX_PHONE_BUTTONS;
});
const canAddCopyCode = computed(() => {
  return (
    copyCodeButtons.value.length < VALIDATION_CONSTANTS.MAX_COPY_CODE_BUTTONS
  );
});
const canAddVoiceCall = computed(() => {
  return (
    voiceCallButtons.value.length < VALIDATION_CONSTANTS.MAX_VOICE_CALL_BUTTONS
  );
});
const menuActionsList = computed(() => {
  if (props.category === 'AUTHENTICATION') {
    return [
      {
        checker: canAddCopyCode.value,
        type: 'COPY_CODE',
        icon: 'i-lucide-copy',
        label: t('WHATSAPP_TEMPLATES.ADD.BUTTONS.ADD_COPY_CODE'),
      },
      {
        checker: canAddUrl.value,
        type: 'URL',
        icon: 'i-lucide-link',
        label: t('WHATSAPP_TEMPLATES.ADD.BUTTONS.ADD_ONE_TAP_AUTOFILL'),
      },
    ];
  }
  return [
    {
      checker: canAddQuickReply.value,
      type: 'QUICK_REPLY',
      icon: 'i-lucide-reply',
      label: t('WHATSAPP_TEMPLATES.ADD.BUTTONS.ADD_QUICK_REPLY'),
    },
    {
      checker: canAddUrl.value,
      type: 'URL',
      icon: 'i-lucide-link',
      label: t('WHATSAPP_TEMPLATES.ADD.BUTTONS.ADD_VISIT_WEBSITE'),
    },
    {
      checker: canAddPhone.value,
      type: 'PHONE_NUMBER',
      icon: 'i-lucide-phone-call',
      label: t('WHATSAPP_TEMPLATES.ADD.BUTTONS.ADD_CALL_PHONE'),
    },
    {
      checker: canAddCopyCode.value,
      type: 'COPY_CODE',
      icon: 'i-lucide-copy',
      label: t('WHATSAPP_TEMPLATES.ADD.BUTTONS.ADD_COPY_CODE'),
    },
    {
      checker: canAddVoiceCall.value,
      type: 'VOICE_CALL',
      icon: 'i-lucide-phone',
      label: t('WHATSAPP_TEMPLATES.ADD.BUTTONS.ADD_VOICE_CALL'),
    },
  ];
});

// ===== Methods =====
function getCopyCodeText() {
  const isArabic = props.language === 'ar';
  const isAuth = props.category === 'AUTHENTICATION';

  if (isArabic) {
    return isAuth ? 'نسخ الرمز' : 'نسخ رمز العرض';
  }
  return isAuth ? 'Copy Code' : 'Copy offer code';
}

function addButton(type) {
  const newButton = {
    id: Date.now(),
    type,
    text: type === 'COPY_CODE' ? getCopyCodeText() : '',
    url: '',
    phone_number: '',
    example: '',
    hasVariable: false,
  };

  buttons.value.push(newButton);
  showAddMenu.value = false;
}
function removeButton(id) {
  const index = buttons.value.findIndex(b => b.id === id);
  if (index !== -1) {
    buttons.value.splice(index, 1);
  }
}
// ===== Validations =====
const rules = computed(() => {
  return {
    buttons: {
      maxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.MAX_COUNT_ERROR',
        maxLength(VALIDATION_CONSTANTS.MAX_TOTAL_BUTTONS)
      ),
      urlMaxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.URL_MAX_COUNT_ERROR',
        () => urlButtons.value.length <= VALIDATION_CONSTANTS.MAX_URL_BUTTONS
      ),
      phoneMaxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.PHONE_MAX_COUNT_ERROR',
        () =>
          phoneButtons.value.length <= VALIDATION_CONSTANTS.MAX_PHONE_BUTTONS
      ),
      copyCodeMaxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.COPY_CODE_MAX_COUNT_ERROR',
        () =>
          copyCodeButtons.value.length <=
          VALIDATION_CONSTANTS.MAX_COPY_CODE_BUTTONS
      ),
      voiceCallMaxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.VOICE_CALL_MAX_COUNT_ERROR',
        () =>
          voiceCallButtons.value.length <=
          VALIDATION_CONSTANTS.MAX_VOICE_CALL_BUTTONS
      ),
      noDuplicateQuickReplyTexts,
      $each: helpers.forEach({
        text: {
          required: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
            required
          ),
          maxLength: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.TEXT_MAX_LENGTH_ERROR',
            maxLength(VALIDATION_CONSTANTS.MAX_BUTTON_TEXT)
          ),
        },
        url: {
          required: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
            (value, button) => {
              return button.type !== 'URL' || !!value;
            }
          ),
          validUrl,
          onlyOneVariable,
          // maxOneVariable,
        },
        phone_number: {
          required: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
            (value, button) => {
              return button.type !== 'PHONE_NUMBER' || !!value;
            }
          ),
          // validPhoneNumber,
          phoneFormat,
          maxLength: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.PHONE_MAX_LENGTH_ERROR',
            maxLength(VALIDATION_CONSTANTS.MAX_PHONE_LENGTH)
          ),
        },
        example: {
          required: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
            (value, button) => {
              // Required for COPY_CODE or URL with variable
              if (button.type === 'COPY_CODE') return !!value;
              if (button.type === 'URL' && button.hasVariable) return !!value;
              return true;
            }
          ),
          maxLength: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.EXAMPLE_MAX_LENGTH_ERROR',
            maxLength(VALIDATION_CONSTANTS.MAX_BUTTON_TEXT)
          ),
          // if (button.type === 'COPY_CODE') {
          //   return !value || value.length <= 15;
          // }
          // return true;
          // }),
        },
      }),
    },
  };
});
const v$ = useVuelidate(rules, { buttons });

async function validateButtons() {
  await v$.value.$validate();
  return v$.value.buttons.$invalid;
}
// ===== Watchers =====
watch(
  () => buttons.value,
  newVal => {
    v$.value.$touch();
    newVal.forEach(btn => {
      if (btn.type === 'URL' && btn.url) {
        btn.hasVariable = btn.url.includes('{{1}}');
      }
    });

    emit('update:modelValue', newVal);
  },
  { deep: true }
);

// ===== Exposed Methods =====
defineExpose({
  validateButtons,
  v$,
});
</script>

<template>
  <div class="mb-6">
    <!-- Empty State -->
    <div
      v-if="buttons.length === 0"
      class="border border-n-weak p-4 rounded-lg flex flex-col gap-4 items-center"
    >
      <p>{{ $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.NO_BUTTONS') }}</p>
    </div>

    <!-- Buttons List -->
    <div v-else>
      <!-- Quick Reply Group -->
      <CollapsibleSection
        v-if="quickReplyButtons.length > 0"
        class="mb-4"
        :title="
          $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.QUICK_REPLIES_TITLE', {
            count: quickReplyButtons.length,
          })
        "
      >
        <template v-for="(button, index) in buttons" :key="button.id">
          <div
            v-if="button.type === 'QUICK_REPLY'"
            class="border border-n-weak p-4 rounded-md mb-3 overflow-hidden"
          >
            <div
              class="flex items-center justify-between border-b border-n-weak py-2 px-3"
            >
              <span
                class="flex items-center gap-2 text-n-slate-12 font-semibold text-sm"
              >
                <Icon icon="i-lucide-reply" :size="16" />
                {{ $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.QUICK_REPLY') }}
              </span>
              <Button
                variant="faded"
                color="ruby"
                size="sm"
                icon="i-lucide-trash"
                @click="removeButton(button.id)"
              />
            </div>
            <div class="p-4">
              <Input
                v-model="button.text"
                :label="$t('WHATSAPP_TEMPLATES.ADD.BUTTONS.BUTTON_TEXT_LABEL')"
                :placeholder="
                  $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.BUTTON_TEXT_PLACEHOLDER')
                "
                :max-length="VALIDATION_CONSTANTS.MAX_BUTTON_TEXT"
                :message-type="
                  v$.buttons.$each.$response?.$errors[index].text?.length
                    ? 'error'
                    : 'info'
                "
                :message="
                  v$.buttons.$each.$response?.$errors[index].text?.length
                    ? $t(
                        v$.buttons.$each.$response?.$errors[index].text[0]
                          .$message,
                        v$.buttons.$each.$response?.$errors[index].text[0]
                          .$params
                      )
                    : ''
                "
              />
            </div>
          </div>
        </template>
      </CollapsibleSection>

      <!-- Call to Action Group -->
      <CollapsibleSection
        v-if="ctaButtons.length > 0"
        class="mb-4"
        :title="
          $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.CTA_TITLE', {
            count: ctaButtons.length,
          })
        "
      >
        <template v-for="(button, index) in buttons" :key="button.id">
          <div
            v-if="
              ['URL', 'PHONE_NUMBER', 'COPY_CODE', 'VOICE_CALL'].includes(
                button.type
              )
            "
            class="border border-n-weak p-4 rounded-md mb-3 overflow-hidden"
          >
            <!-- header -->
            <div
              class="flex items-center justify-between border-b border-n-weak py-2 px-3"
            >
              <span
                class="flex items-center gap-2 text-n-slate-12 font-semibold text-sm"
              >
                <Icon :icon="BUTTONS_INFO_LIST[button.type].icon" :size="16" />
                {{ BUTTONS_INFO_LIST[button.type].name }}
              </span>
              <Button
                variant="faded"
                color="ruby"
                size="sm"
                icon="i-lucide-trash"
                @click="removeButton(button.id)"
              />
            </div>

            <div class="flex gap-4 p-4 *:w-full">
              <Input
                v-model="button.text"
                :label="$t('WHATSAPP_TEMPLATES.ADD.BUTTONS.BUTTON_TEXT_LABEL')"
                :placeholder="
                  $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.BUTTON_TEXT_PLACEHOLDER')
                "
                :max-length="VALIDATION_CONSTANTS.MAX_BUTTON_TEXT"
                :disabled="button.type === 'COPY_CODE'"
                :help-text="
                  button.type === 'COPY_CODE'
                    ? $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.COPY_CODE_TEXT_HINT')
                    : ''
                "
                :message-type="
                  v$.buttons.$each.$response?.$errors[index].text?.length
                    ? 'error'
                    : 'info'
                "
                :message="
                  v$.buttons.$each.$response?.$errors[index].text?.length
                    ? $t(
                        v$.buttons.$each.$response?.$errors[index].text[0]
                          .$message,
                        v$.buttons.$each.$response?.$errors[index].text[0]
                          .$params
                      )
                    : ''
                "
              />

              <!-- URL Fields -->
              <template v-if="button.type === 'URL'">
                <Input
                  v-model="button.url"
                  :label="
                    $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.WEBSITE_URL_LABEL')
                  "
                  :placeholder="
                    $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.WEBSITE_URL_PLACEHOLDER')
                  "
                  :message-type="
                    v$.buttons.$each.$response?.$errors[index]?.url?.length
                      ? 'error'
                      : 'info'
                  "
                  :message="
                    v$.buttons.$each.$response?.$errors[index]?.url?.length
                      ? $t(
                          v$.buttons.$each.$response?.$errors[index]?.url[0]
                            ?.$message,
                          v$.buttons.$each.$response?.$errors[index]?.url[0]
                            ?.$params
                        )
                      : ''
                  "
                />

                <!-- Example field if URL has variable -->
                <Input
                  v-if="button.hasVariable"
                  v-model="button.example"
                  :label="
                    $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.EXAMPLE_VALUE_LABEL')
                  "
                  :placeholder="
                    $t(
                      'WHATSAPP_TEMPLATES.ADD.BUTTONS.EXAMPLE_VALUE_PLACEHOLDER'
                    )
                  "
                  :message-type="
                    v$.buttons.$each.$response?.$errors[index].example?.length
                      ? 'error'
                      : 'info'
                  "
                  :message="
                    v$.buttons.$each.$response?.$errors[index].example?.length
                      ? $t(
                          v$.buttons.$each.$response?.$errors[index].example[0]
                            .$message,
                          v$.buttons.$each.$response?.$errors[index].example[0]
                            .$params
                        )
                      : ''
                  "
                />
              </template>

              <!-- Phone Fields -->
              <template v-if="button.type === 'PHONE_NUMBER'">
                <Input
                  v-model="button.phone_number"
                  :label="
                    $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.PHONE_NUMBER_LABEL')
                  "
                  :placeholder="
                    $t(
                      'WHATSAPP_TEMPLATES.ADD.BUTTONS.PHONE_NUMBER_PLACEHOLDER'
                    )
                  "
                  :max-length="VALIDATION_CONSTANTS.MAX_PHONE_LENGTH"
                  :message-type="
                    v$.buttons.$each.$response?.$errors[index]?.phone_number
                      ?.length
                      ? 'error'
                      : 'info'
                  "
                  :message="
                    v$.buttons.$each.$response?.$errors[index]?.phone_number
                      ?.length
                      ? $t(
                          v$.buttons.$each.$response?.$errors[index]
                            .phone_number[0]?.$message,
                          v$.buttons.$each.$response?.$errors[index]
                            .phone_number[0]?.$params
                        )
                      : ''
                  "
                />
              </template>

              <!-- Copy Code Fields -->
              <template v-if="button.type === 'COPY_CODE'">
                <Input
                  v-model="button.example"
                  :label="
                    $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.EXAMPLE_CODE_LABEL')
                  "
                  :placeholder="
                    $t(
                      'WHATSAPP_TEMPLATES.ADD.BUTTONS.EXAMPLE_CODE_PLACEHOLDER'
                    )
                  "
                  :max-length="VALIDATION_CONSTANTS.MAX_BUTTON_TEXT"
                  :message-type="
                    v$.buttons.$each.$response?.$errors[index].example?.length
                      ? 'error'
                      : 'info'
                  "
                  :message="
                    v$.buttons.$each.$response?.$errors[index].example?.length
                      ? $t(
                          v$.buttons.$each.$response?.$errors[index].example[0]
                            .$message,
                          v$.buttons.$each.$response?.$errors[index].example[0]
                            .$params
                        )
                      : ''
                  "
                />
              </template>

              <!-- Voice Call Fields -->
              <template v-if="button.type === 'VOICE_CALL'">
                <p class="text-xs text-n-slate-11">
                  {{ $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.VOICE_CALL_INFO') }}
                </p>
              </template>
            </div>
          </div>
        </template>
      </CollapsibleSection>
    </div>

    <!-- Add Buttons -->
    <div v-if="canAddMoreButtons" class="flex gap-4 justify-center mt-4">
      <template v-for="btn in menuActionsList" :key="btn.type">
        <Button
          v-if="btn.checker"
          color="blue"
          variant="faded"
          size="md"
          @click="addButton(btn.type)"
        >
          <Icon :icon="btn.icon" :size="18" />
          {{ btn.label }}
        </Button>
      </template>
    </div>
  </div>
</template>
